// CursorHeaderButton — Classic の上部バー (`RoomHeader`) に置く「カーソル」の
// エディタ開閉ボタン (作者 staging フィードバック 2026-09-19 follow-up「ツール
// パネルにあった『カーソル』ボタンを上部バーへ移動したい」)。
//
// 旧実装は `ToolPanel.tsx` の [プレビュ]/[レイヤー]/[反転] と並ぶトグルボタン
// だったが、`RoomHeader` の項目データ (`RoomHeaderItem`) は文字ラベルのみの
// `RoomHeaderAction` か、任意ノードの `RoomHeaderCustom` の 2 種類しかなく、
// 自分のカーソルの 16px サムネ (§A-3 で追加済み) を label 文字列だけでは表現
// できない。そこで `RoomHeaderCustom` の `node` として、`RoomHeaderButton`
// (`RoomHeader.tsx` 内部の非 export component) の見た目 (青系 personal スコープ
// と同じ border/bg/text) を手動で複製したこの component を渡す。
//
// ツールパネルから外すため、ペン/Windows Ink の click 不発対策
// (`useTapActivate`) は元のボタンの堅牢性をそのまま引き継ぐ。
//
// 「New!」バッジ (作者要望 2026-09-19「カーソルの入口ボタンに New! バッジ」):
// 絵ログ共有 / 過去キャンバス / Undo と同じ視覚言語 (accent 塗り + pulse ピル) を
// `showBadge` で出し分ける。消える条件は「どの入口からでもエディタを一度開いたら
// seen」(呼び出し側 `ClassicLayout` が `customCursorNewBadgeSeen.ts` の単一
// localStorage key で判定、Modern `ModernToolRail` と共有)。

import type { CustomCursor } from '@oekaki/protocol';
import { useTapActivate } from '../../hooks/useTapActivate.js';
import { CustomCursorCanvas } from '../shared/CustomCursorCanvas.js';
import { decodeCursorPixels } from '../shared/customCursorPixels.js';

export interface CursorHeaderButtonProps {
  /** 「カーソル」フロートが開いているか (= aria-pressed、開いていれば琥珀アクセント). */
  readonly open: boolean;
  readonly onToggle: () => void;
  /** 自分の確定済みカーソル。`null` (未作成) ならサムネなし (= 文字ラベルのみ). */
  readonly selfCursor: CustomCursor | null;
  /**
   * 「New!」バッジを出すか (2026-09-19 追補)。初回にどの入口からでもカーソン
   * エディタを開くと親 (`ClassicLayout`) が false に切替える。Classic/Modern
   * 共有の 1 本の localStorage key なので、片方の入口で消すともう片方でも消える。
   * 既定 false。
   */
  readonly showBadge?: boolean;
}

export const CursorHeaderButton = ({
  open,
  onToggle,
  selfCursor,
  showBadge = false,
}: CursorHeaderButtonProps): React.ReactNode => {
  const tap = useTapActivate(onToggle);
  const pixels = selfCursor !== null ? decodeCursorPixels(selfCursor) : null;
  const baseTitle = open ? 'カーソルエディタ表示中' : '自分のカーソルを編集';
  return (
    <button
      type="button"
      aria-pressed={open}
      aria-label={showBadge ? `${baseTitle} (新機能)` : undefined}
      data-manual="together.custom-cursor"
      {...tap}
      title={baseTitle}
      // RoomHeaderButton (personal/session スコープ) と同じ見た目 (= 上部バーの
      // 他ボタンと統一感を保つ、`RoomHeader.tsx` の variantClass を手動複製).
      // `relative` はバッジピルの絶対配置の基準にする.
      className={`relative flex shrink-0 items-center gap-1 whitespace-nowrap rounded border px-3 py-0.5 text-[11px] font-bold ${
        open
          ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black hover:brightness-95'
          : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]'
      }`}
    >
      {pixels !== null && <CustomCursorCanvas pixels={pixels} sizePx={16} title="自分のカーソル" />}
      カーソル
      {/* 「New!」バッジ (絵ログ/過去キャンバス/Undo と同じ視覚言語)。意味は
          aria-label の「(新機能)」が担う (ピルは aria-hidden の装飾)。 */}
      {showBadge ? (
        <span
          aria-hidden="true"
          className="-top-1.5 -right-1 absolute animate-pulse rounded-full border border-[var(--color-panel)] bg-[var(--color-accent)] px-1 py-px font-bold text-[8px] text-black leading-none shadow"
        >
          New!
        </span>
      ) : null}
    </button>
  );
};
